/**
 * Thin AgentPermissionGate for the shell
 *
 * This wrapper loads the internal module and calls internal.canUseOgAssist()
 * to gate access. The authorization policy is owned by the internal bundle —
 * consumers ask a yes/no question; the SDK decides which claims are required.
 */

import React, { useEffect, useState } from 'react';

import { loadInternalModule } from '../../loader.js';
import { useAgentPermissionContext } from './AgentPermissionProvider.js';

export interface AgentPermissionGateProps {
  /** Children to render if the user is authorized to use OgAssist */
  children: React.ReactNode;
}

/**
 * Component that conditionally renders children based on whether the user
 * is authorized to use OgAssist. Returns null if not authorized.
 *
 * Must be used within `AgentPermissionProvider`.
 *
 * The authorization check happens asynchronously by loading the internal module
 * and calling its `canUseOgAssist()` function. During the loading/checking phase,
 * nothing is rendered (returns null).
 *
 * @example
 * ```tsx
 * <AgentPermissionProvider entityId={entityId} authToken={token} baseURL={url}>
 *   <AgentPermissionGate>
 *     <MyComponentToShowConditionally />
 *   </AgentPermissionGate>
 * </AgentPermissionProvider>
 * ```
 */
export const AgentPermissionGate: React.FC<AgentPermissionGateProps> = ({ children }) => {
  const ctx = useAgentPermissionContext();
  const [permitted, setPermitted] = useState<boolean | null>(null);

  useEffect(() => {
    let isCancelled = false;

    loadInternalModule(ctx.baseURL)
      .then((internal) => internal.canUseOgAssist(ctx.entityId, ctx.authToken, ctx.baseURL))
      .then((hasPermission) => {
        if (!isCancelled) {
          setPermitted(hasPermission);
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled) {
          console.error('Failed to check OgAssist authorization:', error);
          // On error, deny access (fail closed)
          setPermitted(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [ctx.entityId, ctx.authToken, ctx.baseURL]);

  // While loading or if not authorized, render nothing
  if (permitted !== true) {
    return null;
  }

  return children;
};
